
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getQuestions, hydrateQuestion, getMethods, getKnowledge, hydrateMethod } from '../services/storageService';
import { Question, MethodEntity, KnowledgeEntity } from '../types';
import { ArrowLeft, Edit2, ChevronLeft, ChevronRight, Calendar, BookOpen, BookMarked, PenTool, ImageIcon, Loader2, Layers, Lightbulb, X, Tag } from 'lucide-react';

const ReviewToday = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hydrating, setHydrating] = useState(false);

  // Meta Data State
  const [allMethods, setAllMethods] = useState<MethodEntity[]>([]);
  const [allKnowledge, setAllKnowledge] = useState<KnowledgeEntity[]>([]);
  
  // Detail Modal State
  const [viewingDetail, setViewingDetail] = useState<{item: MethodEntity | KnowledgeEntity, type: 'method' | 'knowledge'} | null>(null);

  // 1. Initial Load & State Restoration
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [allQuestions, ms, ks] = await Promise.all([
            getQuestions(),
            getMethods(),
            getKnowledge()
        ]);
        setAllMethods(ms);
        setAllKnowledge(ks);

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const timestamp = todayStart.getTime();

        const todayQuestions = allQuestions.filter(q => 
            !q.deletedAt && q.createdAt >= timestamp
        ).sort((a, b) => b.createdAt - a.createdAt); // Latest first

        setQuestions(todayQuestions);

        // Restore index if returning from edit
        const state = location.state as any;
        if (state?.restoreState?.currentIndex) {
            const restoredIdx = state.restoreState.currentIndex;
            if (restoredIdx < todayQuestions.length) {
                setCurrentIndex(restoredIdx);
            }
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // Run once on mount

  // 2. Persist Current Index to History
  useEffect(() => {
      if (questions.length > 0) {
          navigate('.', { 
              replace: true, 
              state: { 
                  restoreState: { currentIndex } 
              } 
          });
      }
  }, [currentIndex, questions.length]);

  // Hydrate current question images/content
  useEffect(() => {
      if (questions.length === 0) return;
      
      const currentQ = questions[currentIndex];
      const needsHydration = currentQ.materials.some(m => m === '__IMAGE_REF__') || currentQ.notesImage === '__IMAGE_REF__';
      
      if (needsHydration) {
          setHydrating(true);
          hydrateQuestion(currentQ).then(fullQ => {
              setQuestions(prev => {
                  const next = [...prev];
                  next[currentIndex] = fullQ;
                  return next;
              });
              setHydrating(false);
          });
      }
  }, [currentIndex, questions.length]);

  const handleEdit = () => {
      if (!questions[currentIndex]) return;
      navigate(`/edit/${questions[currentIndex].id}`, { 
          state: { 
              returnTo: '/review-today', 
              restoreState: { currentIndex } 
          } 
      });
  };

  const handleNext = () => {
      if (currentIndex < questions.length - 1) {
          setCurrentIndex(prev => prev + 1);
          window.scrollTo({ top: 0, behavior: 'smooth' });
      }
  };

  const handlePrev = () => {
      if (currentIndex > 0) {
          setCurrentIndex(prev => prev - 1);
          window.scrollTo({ top: 0, behavior: 'smooth' });
      }
  };
  
  const openDetail = async (item: MethodEntity | KnowledgeEntity, type: 'method' | 'knowledge') => {
      let fullItem = item;
      if (type === 'method') {
           fullItem = await hydrateMethod(item as MethodEntity);
      }
      setViewingDetail({ item: fullItem, type });
  };

  const renderWithBold = (text: string) => {
      if (!text) return null;
      return text.split(/(\*\*[\s\S]+?\*\*)/g).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} className="font-bold text-slate-900 bg-yellow-100/50 px-0.5 rounded border-b border-yellow-200/50">{part.slice(2, -2)}</strong>;
          }
          return part;
      });
  };

  // Keyboard navigation
  useEffect(() => {
      const handleKey = (e: KeyboardEvent) => {
          if (e.key === 'ArrowRight') handleNext();
          if (e.key === 'ArrowLeft') handlePrev();
          if (e.key === 'Escape') setViewingDetail(null);
      };
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, questions.length]);

  if (loading) return <div className="flex justify-center p-20 text-slate-400"><Loader2 className="animate-spin mr-2"/> 加载中...</div>;

  if (questions.length === 0) {
      return (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-400">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar size={32} />
              </div>
              <p className="font-bold text-lg text-slate-600">今日暂无错题</p>
              <p className="text-sm mt-1">去刷题或录入新题目吧！</p>
              <button onClick={() => navigate('/')} className="mt-6 text-blue-600 font-bold hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors">返回首页</button>
          </div>
      );
  }

  const q = questions[currentIndex];
  const linkedMethods = q ? allMethods.filter(m => q.methodIds?.includes(m.id)) : [];
  const linkedKnowledge = q ? allKnowledge.filter(k => q.knowledgeIds?.includes(k.id)) : [];

  return (
    <div className="max-w-4xl mx-auto pb-24 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-gray-50 z-20 py-4 border-b border-gray-200 mb-6">
          <div className="flex items-center">
              <button onClick={() => navigate('/')} className="mr-3 text-slate-500 hover:text-slate-800 p-1 rounded-lg hover:bg-gray-200 transition-colors">
                  <ArrowLeft size={20} />
              </button>
              <div>
                  <h1 className="text-lg font-bold text-slate-800 flex items-center">
                      回顾今日错题
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-mono">
                          {currentIndex + 1} / {questions.length}
                      </span>
                  </h1>
              </div>
          </div>
          <button 
              onClick={handleEdit}
              className="flex items-center text-sm font-bold text-blue-600 bg-white border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 shadow-sm transition-colors"
          >
              <Edit2 size={16} className="mr-1.5"/> 编辑
          </button>
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative min-h-[400px]">
          {hydrating && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-blue-100 overflow-hidden">
                  <div className="h-full bg-blue-500 animate-progress"></div>
              </div>
          )}
          
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-100 flex items-center gap-2">
              <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs font-bold uppercase">{q.category}</span>
              {q.subCategory && <span className="bg-cyan-50 text-cyan-700 px-2 py-1 rounded text-xs font-medium border border-cyan-100">{q.subCategory}</span>}
              <span className="flex-1"></span>
              <span className="text-xs text-slate-400 font-mono">{new Date(q.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
          </div>

          <div className="p-6 md:p-8 space-y-6">
              {/* Materials */}
              {q.materials.length > 0 && (
                  <div className="space-y-4 mb-6">
                      {q.materials.map((m, i) => (
                          m === '__IMAGE_REF__' ? 
                          <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse flex items-center justify-center text-slate-300"><ImageIcon size={32}/></div> :
                          <img key={i} src={m} className="w-full h-auto rounded-xl border border-gray-100 object-contain max-h-[500px]" alt="Topic Material" />
                      ))}
                  </div>
              )}

              {/* Text Material */}
              {q.materialText && (
                  <div 
                      className="p-5 bg-gray-50 rounded-xl border-l-4 border-blue-200 text-slate-700 text-lg leading-relaxed rich-text-display mb-6"
                      dangerouslySetInnerHTML={{ __html: q.materialText.replace(/\n/g, '<br/>') }}
                  ></div>
              )}

              {/* Stem */}
              <h2 className="text-xl md:text-2xl font-bold text-slate-800 leading-relaxed whitespace-pre-wrap">{q.stem}</h2>

              {/* Options with Answer Highlight */}
              <div className="space-y-3 mt-6">
                  {q.options.map((opt, idx) => {
                      const isCorrect = idx === q.correctAnswer;
                      return (
                          <div key={idx} className={`flex items-start p-4 rounded-xl border transition-colors ${isCorrect ? 'border-green-500 bg-green-50 text-green-800 shadow-sm' : 'border-gray-100 bg-white text-slate-600'}`}>
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 text-sm font-bold flex-shrink-0 ${isCorrect ? 'bg-green-500 text-white' : 'bg-gray-100 text-slate-500'}`}>
                                  {String.fromCharCode(65 + idx)}
                              </span>
                              <span className={`text-lg leading-relaxed ${isCorrect ? 'font-bold' : ''}`}>{opt}</span>
                              {isCorrect && <span className="ml-auto text-xs font-bold bg-green-200 text-green-800 px-2 py-1 rounded">正确答案</span>}
                          </div>
                      );
                  })}
              </div>
          </div>

          {/* Linked System & Analysis & Notes */}
          <div className="divide-y divide-gray-100">
               {/* Linked Methodology & Knowledge */}
               {(linkedMethods.length > 0 || linkedKnowledge.length > 0) && (
                    <div className="bg-indigo-50/50 p-6 md:p-8">
                        <div className="flex items-center mb-4 text-indigo-800 font-bold text-lg">
                           <Layers size={20} className="mr-2"/> 关联知识体系
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {linkedMethods.map(m => (
                                <button 
                                   key={m.id} 
                                   onClick={() => openDetail(m, 'method')}
                                   className="flex items-center bg-white border border-indigo-200 text-indigo-700 px-3 py-2 rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                                >
                                    <Lightbulb size={16} className="mr-2 text-indigo-500"/> 
                                    {m.title}
                                </button>
                            ))}
                            {linkedKnowledge.map(k => (
                                <button 
                                   key={k.id} 
                                   onClick={() => openDetail(k, 'knowledge')}
                                   className="flex items-center bg-white border border-amber-200 text-amber-700 px-3 py-2 rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                                >
                                    <BookOpen size={16} className="mr-2 text-amber-500"/> 
                                    {k.title || '无标题知识点'}
                                </button>
                            ))}
                        </div>
                    </div>
               )}

              {q.analysis && (
                  <div className="bg-blue-50/50 p-6 md:p-8">
                      <div className="flex items-center mb-4 text-blue-800 font-bold text-lg"><BookOpen size={20} className="mr-2"/> 题目解析</div>
                      <div className="bg-white p-5 rounded-2xl border border-blue-100 text-slate-700 text-base leading-relaxed rich-text-display shadow-sm" dangerouslySetInnerHTML={{ __html: q.analysis }}></div>
                  </div>
              )}

              {(q.noteText || q.notesImage) && (
                  <div className="bg-amber-50/50 p-6 md:p-8">
                      <div className="flex items-center mb-4 text-amber-800 font-bold text-lg"><BookMarked size={20} className="mr-2"/> 个人笔记</div>
                      {q.noteText && <div className="mb-4 bg-white p-5 rounded-2xl border border-amber-100 text-slate-700 text-base leading-relaxed rich-text-display shadow-sm" dangerouslySetInnerHTML={{ __html: q.noteText }}></div>}
                      {!q.noteText && q.notesImage && (
                          <div className="bg-white p-2 rounded-2xl border border-amber-200 shadow-sm inline-block">
                              <img src={q.notesImage === '__IMAGE_REF__' ? '' : q.notesImage} className="max-w-full rounded-xl" alt="Note"/>
                          </div>
                      )}
                  </div>
              )}
          </div>
      </div>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur border-t border-gray-200 lg:pl-20 z-10">
          <div className="max-w-4xl mx-auto flex justify-between items-center gap-4">
              <button 
                  onClick={handlePrev} 
                  disabled={currentIndex === 0}
                  className="flex-1 flex items-center justify-center py-3 rounded-xl font-bold text-slate-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                  <ChevronLeft size={20} className="mr-1"/> 上一题
              </button>
              <span className="text-sm font-bold text-slate-400 hidden sm:block">
                  支持键盘 ← → 翻页
              </span>
              <button 
                  onClick={handleNext} 
                  disabled={currentIndex === questions.length - 1}
                  className="flex-1 flex items-center justify-center py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:shadow-none transition-all active:scale-95"
              >
                  下一题 <ChevronRight size={20} className="ml-1"/>
              </button>
          </div>
      </div>

      {/* Detail Modal */}
      {viewingDetail && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 animate-in fade-in duration-200" onClick={(e) => { if(e.target === e.currentTarget) setViewingDetail(null); }}>
             <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
                 <div className="p-5 border-b border-gray-100 flex justify-between items-start bg-gray-50/80 backdrop-blur-sm rounded-t-2xl">
                      <div>
                          <div className="flex items-center gap-2 mb-1">
                              <h2 className="text-xl font-bold text-slate-800 leading-tight">{viewingDetail.item.title || '未命名'}</h2>
                              {viewingDetail.type === 'method' ? (
                                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded border border-indigo-200 font-bold">方法论</span>
                              ) : (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded border border-amber-200 font-bold">知识点</span>
                              )}
                          </div>
                          {(viewingDetail.item as any).tag && (
                              <div className="flex items-center text-xs text-slate-500 mt-1">
                                  <Tag size={12} className="mr-1"/> {(viewingDetail.item as any).tag}
                              </div>
                          )}
                      </div>
                      <button onClick={() => setViewingDetail(null)} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-gray-200 rounded-full transition-colors"><X size={24}/></button>
                 </div>
                 <div className="p-6 overflow-y-auto custom-scrollbar">
                      {/* Render Content based on type */}
                      {viewingDetail.type === 'knowledge' && (viewingDetail.item as KnowledgeEntity).idiomData ? (
                          <div className="space-y-4">
                                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                                    <h4 className="font-bold text-purple-900 text-sm mb-1">本意</h4>
                                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{renderWithBold((viewingDetail.item as KnowledgeEntity).idiomData?.meaning || '')}</p>
                                </div>
                                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                    <h4 className="font-bold text-blue-900 text-sm mb-1">用法</h4>
                                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{renderWithBold((viewingDetail.item as KnowledgeEntity).idiomData?.usage || '')}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                    <h4 className="font-bold text-gray-700 text-sm mb-1">例句</h4>
                                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{renderWithBold((viewingDetail.item as KnowledgeEntity).idiomData?.example || '')}</p>
                                </div>
                          </div>
                      ) : (
                          <div className="rich-text-display text-slate-700 text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: viewingDetail.item.content }}></div>
                      )}
                 </div>
                 <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end">
                      <button onClick={() => setViewingDetail(null)} className="px-5 py-2 bg-white border border-gray-200 text-slate-600 rounded-lg font-bold shadow-sm hover:bg-gray-100">关闭</button>
                 </div>
             </div>
        </div>
      )}
    </div>
  );
};

export default ReviewToday;
